import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileText, Upload, ListChecks, BarChart3, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import StartRunScreen from "./screens/StartRunScreen";
import UploadDataScreen from "./screens/UploadDataScreen";
import ValidationScreen from "./screens/ValidationScreen";
import GenerateScreen from "./screens/GenerateScreen";
import DashboardScreen from "./screens/DashboardScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, name: "Start", path: "/", icon: FileText },
  { id: 2, name: "Upload", path: "/upload", icon: Upload },
  { id: 3, name: "Validate", path: "/validate", icon: ListChecks },
  { id: 4, name: "Review", path: "/review", icon: Check },
  { id: 5, name: "Dashboard", path: "/dashboard", icon: BarChart3 },
];

export const StepWizard = ({ currentStep }) => {
  return (
    <div className="step-wizard py-4" data-testid="step-wizard">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = step.id === currentStep;
        const isCompleted = step.id < currentStep;
        
        return (
          <div key={step.id} className="step-item">
            <div
              className={`step-circle ${isActive ? "active" : isCompleted ? "completed" : "pending"}`}
              data-testid={`step-${step.id}`}
            >
              {isCompleted ? <Check size={16} /> : <Icon size={16} />}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${isActive ? "text-[#062B55]" : "text-slate-400"}`}>
              {step.name}
            </span>
            {index < STEPS.length - 1 && (
              <div className={`step-connector ${isCompleted ? "completed" : ""}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const Header = ({ currentStep }) => {
  const navigate = useNavigate();
  
  return (
    <header className="app-header">
      <div className="app-header-content">
        <div 
          className="app-logo cursor-pointer" 
          onClick={() => navigate("/")}
          data-testid="app-logo"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_dc61d6d0-8f14-4cc5-9f15-757031b937fc/artifacts/4dwenw6e_auditbuddy_transperant_lightbg.svg" 
            alt="AuditBuddy" 
            className="h-8 w-auto object-contain"
          />
        </div>
        <div className="ml-auto">
          <StepWizard currentStep={currentStep} />
        </div>
      </div>
    </header>
  );
};

export default function RunWizard() {
  const [runId, setRunId] = useState(null);
  const [runData, setRunData] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [validation, setValidation] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getCurrentStep = () => {
    const path = location.pathname;
    const step = STEPS.find(s => s.path === path);
    return step ? step.id : 1;
  };

  const currentStep = getCurrentStep();

  // Fetch run data when runId changes
  useEffect(() => {
    if (runId) {
      fetchRunData();
    }
  }, [runId]);

  const fetchRunData = async () => {
    if (!runId) return;
    try {
      const [runRes, uploadsRes] = await Promise.all([
        axios.get(`${API}/runs/${runId}`),
        axios.get(`${API}/runs/${runId}/uploads`)
      ]);
      setRunData(runRes.data);
      setUploads(uploadsRes.data);
    } catch (error) {
      console.error("Error fetching run data:", error);
    }
  };

  const createRun = async (data) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/runs`, data);
      setRunId(response.data.run_id);
      setRunData(response.data);
      toast.success("Run created successfully");
      navigate("/upload");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create run";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (fileType, file) => {
    if (!runId) {
      toast.error("Please start a run first");
      return;
    }
    
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/runs/${runId}/upload/${fileType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Update uploads state
      setUploads(prev => {
        const filtered = prev.filter(u => u.file_type !== fileType);
        return [...filtered, response.data];
      });
      
      if (response.data.schema_valid) {
        toast.success(`${fileType} file uploaded successfully`);
      } else {
        toast.error(`${fileType}: ${response.data.blocking_error}`);
      }
      
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || `Failed to upload ${fileType}`;
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchValidation = async () => {
    if (!runId) return null;
    try {
      const response = await axios.get(`${API}/runs/${runId}/validation`);
      setValidation(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching validation:", error);
      return null;
    }
  };

  const generateExceptions = async () => {
    if (!runId) {
      toast.error("No run found");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/runs/${runId}/generate`);
      setExceptions(response.data);
      toast.success(`Generated ${response.data.exception_count} exceptions`);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to generate exceptions";
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchExceptions = async () => {
    if (!runId) return null;
    try {
      const response = await axios.get(`${API}/runs/${runId}/exceptions`);
      setExceptions(response.data);
      return response.data;
    } catch (error) {
      // 404 is expected if exceptions haven't been generated yet
      if (error.response?.status !== 404) {
        console.error("Error fetching exceptions:", error);
      }
      return null;
    }
  };

  const exportExceptions = async (readyConfirmed) => {
    if (!runId) {
      toast.error("No run found");
      return;
    }
    
    if (!readyConfirmed) {
      toast.error("Please confirm 'Ready for interviews' to export.");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/runs/${runId}/export?ready_confirmed=${readyConfirmed}`,
        {},
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `exception_list_${runId}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export completed successfully");
      
      // Refresh exceptions to get updated timestamps
      await fetchExceptions();
      
      return true;
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to export";
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchTtFDEL = async () => {
    if (!runId) return null;
    try {
      const response = await axios.get(`${API}/runs/${runId}/ttfdel`);
      return response.data;
    } catch (error) {
      console.error("Error fetching TtFDEL:", error);
      return null;
    }
  };

  const resetWizard = () => {
    setRunId(null);
    setRunData(null);
    setUploads([]);
    setValidation(null);
    setExceptions(null);
    navigate("/");
  };

  return (
    <>
      <Header currentStep={currentStep} />
      <main className="app-container animate-fadeIn">
        <Routes>
          <Route
            path="/"
            element={
              <StartRunScreen
                onSubmit={createRun}
                loading={loading}
              />
            }
          />
          <Route
            path="/upload"
            element={
              <UploadDataScreen
                runId={runId}
                uploads={uploads}
                onUpload={uploadFile}
                onContinue={() => navigate("/validate")}
                onBack={() => navigate("/")}
                loading={loading}
              />
            }
          />
          <Route
            path="/validate"
            element={
              <ValidationScreen
                runId={runId}
                validation={validation}
                uploads={uploads}
                onFetchValidation={fetchValidation}
                onProceed={() => navigate("/review")}
                onBack={() => navigate("/upload")}
                loading={loading}
              />
            }
          />
          <Route
            path="/review"
            element={
              <GenerateScreen
                runId={runId}
                exceptions={exceptions}
                onGenerate={generateExceptions}
                onFetchExceptions={fetchExceptions}
                onExport={() => navigate("/dashboard")}
                onBack={() => navigate("/validate")}
                loading={loading}
              />
            }
          />
          <Route
            path="/dashboard"
            element={
              <DashboardScreen
                runId={runId}
                runData={runData}
                exceptions={exceptions}
                onExport={exportExceptions}
                onFetchExceptions={fetchExceptions}
                onNewRun={resetWizard}
                onBack={() => navigate("/review")}
                loading={loading}
              />
            }
          />
        </Routes>
      </main>
    </>
  );
}
